import type { GatewayRoute } from './types';

/**
 * Block 03 / U153 — exactly four independent first-level routes.
 * Order is fixed; a fifth general card is forbidden without an owner decision.
 *
 * Each route maps to a real 1.83 internal module (not a modal stub):
 *   RESEARCH  → Research master / OwnerV8 request wizard (U149 · 11×72)
 *   AUTHOR    → AU123 author / co-author overlay
 *   EDITORIAL → R5.14 conference / journal editorial project
 *   CONTENT   → Publisher 1.81 (Academia / Catalog / Social handoff)
 */
export interface GatewayRouteDefinition {
  id: GatewayRoute;
  etalonKey: 'research' | 'author' | 'editorial' | 'content';
  moduleId: 'U149_RESEARCH' | 'AU123' | 'R514_EDITORIAL' | 'PUBLISHER_181';
  code: string;
  title: string;
  text: string;
  accent: string;
  iconPath: string;
  bullets: string[];
  openCta: string;
}

export const GATEWAY_FIRST_LEVEL_ROUTES: readonly GatewayRouteDefinition[] = [
  {
    id: 'RESEARCH',
    etalonKey: 'research',
    moduleId: 'U149_RESEARCH',
    code: '01',
    title: 'Научно-исследовательские услуги',
    text: 'Каталог научно-исследовательских услуг и канонический пятиэтапный маршрут.',
    accent: '#41d2ff',
    iconPath: 'M9 3h6 M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3 M8 15h8',
    bullets: [
      'Этап 1: направление и услуга',
      'Этап 2: сведения и материалы',
      'Этапы 3–5: проверка и отправка',
    ],
    openCta: 'Открыть маршрут',
  },
  {
    id: 'AUTHOR',
    etalonKey: 'author',
    moduleId: 'AU123',
    code: '02',
    title: 'Статья, авторство и соавторство',
    text: 'Публикационное сопровождение, готовые статьи, данные автора и поиск соавторов.',
    accent: '#9e80ff',
    iconPath: 'M5 4h14v16H5z M8 8h8 M8 12h8 M8 16h5 M17 5l3 3-6 6-4 1 1-4z',
    bullets: ['Авторские маршруты', 'Готовые статьи', 'Анкета автора'],
    openCta: 'Открыть маршрут',
  },
  {
    id: 'EDITORIAL',
    etalonKey: 'editorial',
    moduleId: 'R514_EDITORIAL',
    code: '03',
    title: 'Создать журнал или конференцию',
    text: 'Редакционный проект, внутренний приём материалов и роли редакторов.',
    accent: '#e2ad3a',
    iconPath: 'M4 5h16v15H4z M7 3v4 M17 3v4 M4 9h16 M8 13h3 M13 13h3 M8 16h8',
    bullets: ['Конференция / мероприятие', 'Журнал', 'Внутренний приём материалов'],
    openCta: 'Открыть маршрут',
  },
  {
    id: 'CONTENT',
    etalonKey: 'content',
    moduleId: 'PUBLISHER_181',
    code: '04',
    title: 'Опубликовать контент',
    text: 'Единый издательский центр для ленты, публичных каталогов и соцсетей.',
    accent: '#49ddb7',
    iconPath: 'M4 5h16v14H4z M7 9h10 M7 13h6 M16 13h1 M7 17h8',
    bullets: [
      'Опубликовать в ленте платформы',
      'Добавить в публичный каталог',
      'Передать в соцсети / сайт ЕНУ',
    ],
    openCta: 'Открыть издательский центр',
  },
] as const;

/** Hard contract: hub always defines exactly these four routes. */
export const GATEWAY_ROUTE_COUNT = 4;

export function assertFourGatewayRoutes(): void {
  if (GATEWAY_FIRST_LEVEL_ROUTES.length !== GATEWAY_ROUTE_COUNT) {
    throw new Error(
      `Create Request Hub must expose exactly ${GATEWAY_ROUTE_COUNT} first-level routes`
    );
  }
  const ids = GATEWAY_FIRST_LEVEL_ROUTES.map((r) => r.id);
  if (
    ids[0] !== 'RESEARCH' ||
    ids[1] !== 'AUTHOR' ||
    ids[2] !== 'EDITORIAL' ||
    ids[3] !== 'CONTENT'
  ) {
    throw new Error('Create Request Hub route order must be RESEARCH → AUTHOR → EDITORIAL → CONTENT');
  }
}

export function getGatewayRoute(id: GatewayRoute): GatewayRouteDefinition {
  const route = GATEWAY_FIRST_LEVEL_ROUTES.find((r) => r.id === id);
  if (!route) {
    throw new Error(`Unknown Create Request Hub route: ${id}`);
  }
  return route;
}
